/**
 * Thanksgiving posters — authored via the `/pack` toile process (plan
 * `thinking/plans/2026_08_31_toile_pack_wiring.md` Track B), wave `toile_seasons`. Narcis file-split
 * shape (Track A): one module per pack, `registry.ts` assembles them into `livePacks`.
 */
import {tagline, chips, type ToilePack} from './data'

export const thanksgivingPostersPack: ToilePack = {
    slug: 'thanksgiving-posters',
    category: 'season',
    name: 'Thanksgiving posters',
    tagline: tagline('pieces', 'themes', 'styles'),
    chips: chips('themes', 'styles'),
    ratio: {w: 832, h: 1216},
    readings: [
        {key: 'turkey', label: 'Big turkey', subject: 'A large turkey with a fanned tail of layered feathers filling the poster, no text'},
        {key: 'cornucopia', label: 'Cornucopia', subject: 'A cornucopia spilling pumpkins, apples and wheat across the poster, no text'},
        {key: 'greeting', label: 'Greeting', subject: 'A poster with "GIVE THANKS" lettered large in a warm serif with a leaf border and nothing else'},
    ],
    styles: [
        {key: 'harvest', label: 'Harvest flat', style: 'flat harvest illustration, simplified shapes, no outlines, matte colour', palette: 'Mustard, rust, pumpkin orange, olive and cream', framing: 'Centered on a solid cream background, built to read clean at small size'},
        {key: 'vintage', label: 'Vintage americana', style: 'vintage americana illustration, halftone grain, slight misregistration, warm', palette: 'Faded rust, brown, mustard and cream', framing: 'Centered on aged cream paper with an even margin'},
        {key: 'watercolor', label: 'Watercolor', style: 'soft watercolour illustration, loose edges, paper texture showing', palette: 'Warm autumn washes of orange, ochre and brown', framing: 'Centered with white paper around it'},
    ],
    directions: ['a fox and pumpkins instead', 'for a farmhouse kitchen wall', 'in a darker autumn palette'],
    directionHint: 'try: a fox and pumpkins instead',
    seo: {
        title: 'Thanksgiving Posters: Turkey, Cornucopia & Greeting',
        metaDescription: 'Nine Thanksgiving posters — big turkey, cornucopia, Give Thanks greeting — in harvest flat, vintage americana, and watercolor styles.',
        h1: 'Thanksgiving poster designs: turkey, cornucopia, give-thanks greeting',
        tagline: 'Nine posters, three subjects, three treatments each.',
        intro: 'Nine bold, flat Thanksgiving posters, built to read from across a room: a turkey filling the frame with layered feathers, a cornucopia spilling pumpkins and wheat, and a Give Thanks greeting in warm lettering — each in a harvest flat, a vintage americana, and a watercolor treatment. Printed bleed to edge for a wall or window.',
        useCases: [
            'a restaurant or storefront window display',
            'a home wall print for the holiday season',
            'a classroom or community bulletin board',
            'a printable poster for a Thanksgiving event',
        ],
        faq: [
            {q: 'What size do these posters print at?', a: 'Each poster is a tall vertical format, bled to the edge, so it scales cleanly from a small print to a full wall poster.'},
            {q: 'Is there any lettering I need to add myself?', a: "The greeting poster already carries 'Give Thanks' lettered in; the turkey and cornucopia posters have no text so they work as pure decor."},
            {q: "What's the difference between the three styles?", a: 'Harvest flat uses simplified matte shapes, vintage americana adds halftone grain and warm faded color, and watercolor uses soft loose edges with visible paper texture.'},
            {q: 'Can I print these for a shop or restaurant?', a: 'Yes — the posters are cleared for commercial use, including storefront and restaurant display.'},
        ],
    },
}
